import React, { useEffect } from "react";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wind, WashingMachine, Paintbrush, DoorOpen, Droplets, SprayCan, Wrench,
  ArrowRight, Check, ArrowLeft,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useLang, CATEGORIES, SERVICE_PAGES } from "../i18n";

const iconMap = { Wind, WashingMachine, Paintbrush, DoorOpen, Droplets, SprayCan, Wrench };

const BULLETS = {
  en: {
    ac: ["No-cool diagnostics", "Thermostat swaps", "Tune-ups & filter changes", "Duct airflow issues"],
    appliances: ["Dishwashers", "Refrigerators", "Washers & dryers", "Ovens & stoves"],
    paint: ["Full rooms & accents", "Ceilings & trim", "Patch, prime, paint", "Post-move touch-ups"],
    windows: ["Weatherstripping & seals", "Sliding-door tracks", "Locks, hinges, hardware", "Screens & rollers"],
    plumbing: ["Leaky faucets & cartridges", "Running toilets", "Garbage disposals", "Shut-off valves"],
    pressure: ["Driveways & walkways", "House siding & fences", "Pool cages & patios", "Soft-wash on delicate surfaces"],
    general: ["TV & shelf mounting", "Furniture assembly", "Blinds & curtain rods", "Doorbells & smart devices"],
  },
  es: {
    ac: ["Diagnóstico sin frío", "Cambio de termostato", "Mantenimientos y filtros", "Problemas de ductos"],
    appliances: ["Lavaplatos", "Neveras", "Lavadoras y secadoras", "Hornos y estufas"],
    paint: ["Cuartos completos y acentos", "Techos y molduras", "Parchear, imprimar, pintar", "Retoques post-mudanza"],
    windows: ["Sellos y burletes", "Correderas de puerta", "Cerraduras, bisagras, herrajes", "Mallas y rodillos"],
    plumbing: ["Grifos con fuga y cartuchos", "Sanitarios que corren", "Trituradores de basura", "Llaves de paso"],
    pressure: ["Entradas y aceras", "Fachadas y cercas", "Jaulas de piscina y patios", "Lavado suave en superficies delicadas"],
    general: ["Montaje de TV y estantes", "Ensamblaje de muebles", "Persianas y cortineros", "Timbres y dispositivos inteligentes"],
  },
};

const HERO_IMG = {
  ac: "https://images.unsplash.com/photo-1762341123870-d706f257a12e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&h=900&fit=crop",
  appliances: "https://images.pexels.com/photos/3829549/pexels-photo-3829549.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600&h=900",
  paint: "https://images.pexels.com/photos/6474471/pexels-photo-6474471.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600&h=900",
  windows: "https://images.unsplash.com/photo-1513584684374-8bab748fbf90?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&h=900&fit=crop",
  plumbing: "https://images.unsplash.com/photo-1595515770330-ceeea7d82cfd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&h=900&fit=crop",
  pressure: "https://images.unsplash.com/photo-1781637202423-33ec5b47e52e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&h=900&fit=crop",
  general: "https://images.unsplash.com/photo-1645651964715-d200ce0939cc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&h=900&fit=crop",
};

export default function ServicePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLang();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const cat = CATEGORIES.find((c) => c.id === id);
  if (!cat) return <Navigate to="/" replace />;

  const info = t.services.items[id];
  const seo = SERVICE_PAGES[lang][id];
  const bullets = BULLETS[lang][id];
  const Icon = iconMap[cat.icon] || Wrench;
  const priceLabel = info.price ? `${t.services.from} ${info.price}` : info.quote;

  return (
    <div className="bg-crema">
      <SEO
        title={seo.title}
        description={seo.desc}
        path={`/services/${id}`}
        lang={lang}
        image={HERO_IMG[id]}
      />
      <Header />

      <main className="pt-24 md:pt-28">
        {/* Hero */}
        <section className="relative mx-auto max-w-7xl px-5 md:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center py-10 md:py-16">
          <div>
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-warmgray hover:text-charcoal text-sm font-semibold mb-6 transition-colors"
              data-testid="service-back"
            >
              <ArrowLeft className="h-4 w-4" /> {lang === "es" ? "Todos los servicios" : "All services"}
            </button>
            <span className="inline-flex items-center gap-2 rounded-full bg-teal/10 text-teal px-3 py-1 text-xs font-bold uppercase tracking-widest">
              <Icon className="h-4 w-4" /> {info.name}
            </span>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mt-5 font-display font-black tracking-tight leading-[1.02] text-charcoal text-4xl md:text-6xl"
              data-testid="service-title"
            >
              {seo.intro}
            </motion.h1>
            <p className="mt-6 text-lg md:text-xl text-warmgray max-w-xl leading-relaxed">
              {seo.body}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                onClick={() => navigate(`/book?category=${id}`)}
                className="group inline-flex items-center gap-3 rounded-full bg-coral text-crema text-base font-semibold pl-7 pr-6 py-4 hover:brightness-105 transition-all duration-300 shadow-[0_10px_40px_-12px_rgba(216,90,48,0.7)]"
                data-testid="service-cta"
              >
                {lang === "es" ? "Reservar este servicio" : "Book this service"}
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <span className="inline-flex items-center gap-2 rounded-full bg-crema border border-charcoal/10 px-4 py-2 text-charcoal text-sm">
                <span className="text-warmgray">{info.price ? t.services.from : ""}</span>
                <span className="font-bold text-teal">{info.price || info.quote}</span>
              </span>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-[0_40px_80px_-40px_rgba(43,42,40,0.5)]"
          >
            <img src={HERO_IMG[id]} alt={info.name} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute top-4 left-4 inline-flex items-baseline gap-1 rounded-full bg-crema/95 backdrop-blur-sm px-3 py-1.5 shadow-md">
              <span className="text-[11px] font-medium text-warmgray">{info.price ? t.services.from : ""}</span>
              <span className="text-sm font-bold text-teal">{info.price || info.quote}</span>
            </div>
          </motion.div>
        </section>

        {/* Includes */}
        <section className="py-16 md:py-24 bg-crema">
          <div className="mx-auto max-w-7xl px-5 md:px-10">
            <h2 className="font-display font-black tracking-tight text-charcoal text-3xl md:text-4xl">
              {lang === "es" ? "Lo que cubrimos" : "What we cover"}
            </h2>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {bullets.map((b, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 rounded-2xl border border-charcoal/10 bg-white p-5"
                  data-testid={`service-bullet-${i}`}
                >
                  <span className="mt-0.5 grid place-items-center h-6 w-6 rounded-full bg-teal text-crema shrink-0">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                  <span className="text-charcoal text-base font-medium leading-snug">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related services */}
        <section className="py-12 md:py-20 bg-crema">
          <div className="mx-auto max-w-7xl px-5 md:px-10">
            <h3 className="font-display font-bold text-charcoal text-xl md:text-2xl mb-6">
              {lang === "es" ? "Otros servicios" : "Other services"}
            </h3>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.filter((c) => c.id !== id).map((c) => (
                <Link
                  key={c.id}
                  to={`/services/${c.id}`}
                  className="inline-flex items-center gap-2 rounded-full border border-charcoal/10 bg-white px-4 py-2 text-sm text-charcoal hover:border-teal transition-colors"
                  data-testid={`related-${c.id}`}
                >
                  {t.services.items[c.id].name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="py-20 md:py-32 bg-crema text-center">
          <div className="mx-auto max-w-3xl px-5 md:px-10">
            <h2 className="font-display font-black tracking-tight text-charcoal text-3xl md:text-5xl">
              {t.closing.headline}
            </h2>
            <div className="mt-8">
              <button
                onClick={() => navigate(`/book?category=${id}`)}
                className="inline-flex items-center gap-3 rounded-full bg-teal text-crema text-lg font-semibold pl-8 pr-7 py-5 hover:brightness-110 transition-all duration-300 shadow-[0_16px_50px_-16px_rgba(15,110,86,0.7)]"
                data-testid="service-closing-cta"
              >
                {t.closing.cta}
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
